import React, { useState, useEffect } from 'react';
import { dashboardApi } from '../api';
import { Card, PageTitle, Table, Badge, Btn, Select, Input, Pagination } from '../components/common/UI';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const levelColor = { info: 'blue', warn: 'yellow', error: 'red' };

export default function Logs() {
  const [tab, setTab] = useState('system');
  const [logs, setLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorByCategory, setErrorByCategory] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, limit: 50, level: '', category: '', search: '' });
  const [errFilters, setErrFilters] = useState({ hours: 24, category: '' });

  const loadSystem = async (f) => {
    const usedFilters = f || filters;
    setLoading(true);
    try {
      const params = {};
      Object.keys(usedFilters).forEach((k) => { if (usedFilters[k] !== '') params[k] = usedFilters[k]; });
      const { data } = await dashboardApi.logs(params);
      setLogs(data.data.logs);
      setStats(data.data.stats || {});
      setPagination(data.data.pagination);
    } catch (_e) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const loadErrors = async (f) => {
    const usedFilters = f || errFilters;
    setLoading(true);
    try {
      const { data } = await dashboardApi.errorLogs(usedFilters);
      setErrorLogs(data.data.logs);
      setErrorByCategory(data.data.byCategory || []);
    } catch (_e) {
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'system') loadSystem();
    else loadErrors();
  }, [tab]);

  const handleFilter = (key, val) => {
    const f = Object.assign({}, filters, { [key]: val, page: key === 'page' ? val : 1 });
    setFilters(f);
    loadSystem(f);
  };

  const handleErrFilter = (key, val) => {
    const f = Object.assign({}, errFilters, { [key]: val });
    setErrFilters(f);
    loadErrors(f);
  };

  return (
    <div>
      <PageTitle sub="System and error logs">Logs</PageTitle>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['system', 'errors'].map((t) => (
          <Btn key={t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)}>
            {t === 'system' ? 'System Logs' : 'Error Logs'}
          </Btn>
        ))}
      </div>

      {tab === 'system' && (
        <Card>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {[['info', '#3b82f6'], ['warn', '#f59e0b'], ['error', '#ef4444']].map((item) => (
              <div key={item[0]} style={{ padding: '6px 14px', background: item[1] + '18', borderRadius: 6, border: '1px solid ' + item[1] + '40' }}>
                <span style={{ color: item[1], fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                  {item[0]}: {stats[item[0]] || 0}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ flex: '0 0 130px' }}>
              <Select label="Level" value={filters.level} onChange={(e) => handleFilter('level', e.target.value)}>
                <option value="">All</option>
                {['info', 'warn', 'error'].map((l) => <option key={l} value={l}>{l}</option>)}
              </Select>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <Input label="Search" placeholder="Search messages..." value={filters.search}
                onChange={(e) => handleFilter('search', e.target.value)} />
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <Input label="Category" placeholder="e.g. whatsapp" value={filters.category}
                onChange={(e) => handleFilter('category', e.target.value)} />
            </div>
            <Btn size="sm" variant="secondary" onClick={() => loadSystem()} style={{ alignSelf: 'flex-end', marginBottom: 16 }}>
              <RefreshCw size={14} />
            </Btn>
          </div>
          <Table
            loading={loading}
            headers={['Time', 'Level', 'Category', 'Action', 'Message']}
            rows={logs.map((l) => [
              <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{new Date(l.timestamp).toLocaleString()}</span>,
              <Badge color={levelColor[l.level] || 'default'}>{l.level}</Badge>,
              <Badge>{l.category}</Badge>,
              <code style={{ fontSize: 11, color: '#94a3b8' }}>{l.action}</code>,
              <span style={{ fontSize: 12 }}>{l.message}</span>,
            ])}
          />
          <Pagination page={pagination.page} pages={pagination.pages} onPage={(p) => handleFilter('page', p)} />
        </Card>
      )}

      {tab === 'errors' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: '0 0 140px' }}>
              <Select label="Time Range" value={errFilters.hours} onChange={(e) => handleErrFilter('hours', e.target.value)}>
                {[1, 6, 12, 24, 48, 168].map((h) => <option key={h} value={h}>{h}h</option>)}
              </Select>
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <Input label="Category" placeholder="Filter..." value={errFilters.category}
                onChange={(e) => handleErrFilter('category', e.target.value)} />
            </div>
            <Btn size="sm" variant="secondary" onClick={() => loadErrors()} style={{ alignSelf: 'flex-end', marginBottom: 16 }}>
              <RefreshCw size={14} />
            </Btn>
          </div>

          {errorByCategory.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              {errorByCategory.map((c) => (
                <div key={c._id} style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
                  <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 600 }}>{c._id}: {c.count}</span>
                </div>
              ))}
            </div>
          )}

          <Card>
            <Table
              loading={loading}
              headers={['Time', 'Category', 'Action', 'Message', 'Details']}
              rows={errorLogs.map((l) => [
                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{new Date(l.timestamp).toLocaleString()}</span>,
                <Badge color="red">{l.category}</Badge>,
                <code style={{ fontSize: 11, color: '#94a3b8' }}>{l.action}</code>,
                <span style={{ fontSize: 12, color: '#fca5a5' }}>{l.message}</span>,
                l.details && Object.keys(l.details).length > 0
                  ? <code style={{ fontSize: 10, color: '#64748b' }}>{JSON.stringify(l.details).slice(0, 60)}</code>
                  : null,
              ])}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
